import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import MainLayout from '../../components/MainLayout';
import { useAutoRefresh } from '../../hooks/useAutoRefresh';
import { useSystemSettings } from '../../context/SystemSettingsContext';
import { fetchRealtimeShotStatus, saveSystemSettings } from '../../services/api';
import './RealtimeShotStatus.css';

// 변경 감지 옵션
const CHANGE_OPTIONS = [
  { key: 'highlight', label: '변경셀 강조' },
  { key: 'delta', label: '변화량 표시' },
  { key: 'sortByUpdate', label: '업데이트순 정렬' },
];

// 컬럼 정의 (DB에 있는 컬럼만)
const SHOT_COLUMNS = [
  { key: 'equipmentName', label: '사출기명', fixed: true },
  { key: 'craftId', label: '제품명' },
  { key: 'shotCount', label: '샷카운트' },
  { key: 'updateDateTime', label: '업데이트시간' },
  { key: 'tmInjSpeed1', label: '사출속도' },
  { key: 'tmInjPress1', label: '사출압력' },
  { key: 'tmInjPosi1', label: '사출위치' },
  { key: 'tmHoldSpeed1', label: '보압속도' },
  { key: 'tmHoldPress1', label: '보압압력' },
  { key: 'tmChargePress1', label: '계량압력' },
  { key: 'tmChargePosi1', label: '계량위치' },
  { key: 'tmInjEndPosi', label: '사출종료위치' },
  { key: 'tmTemp1Current', label: '온도1' },
  { key: 'tmTemp2Current', label: '온도2' },
  { key: 'tmTemp3Current', label: '온도3' },
  { key: 'tmTemp4Current', label: '온도4' },
  { key: 'tmTemp5Current', label: '온도5' },
];

function RealtimeShotStatus() {
  const navigate = useNavigate();
  const { settings, loading: settingsLoading, refreshSettings } = useSystemSettings();
  const [shotData, setShotData] = useState([]);
  const [prevShotData, setPrevShotData] = useState([]);
  const [changedCells, setChangedCells] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(new Date());
  const [changeOptions, setChangeOptions] = useState({
    highlight: true,
    delta: false,
    sortByUpdate: false,
  });
  const [optionsInitialized, setOptionsInitialized] = useState(false);
  const refreshIndicatorRef = useRef(null);

  // ref로 최신 상태 유지 (클로저 문제 해결)
  const shotDataRef = useRef(shotData);
  const changeOptionsRef = useRef(changeOptions);

  useEffect(() => {
    shotDataRef.current = shotData;
  }, [shotData]);

  useEffect(() => {
    changeOptionsRef.current = changeOptions;
  }, [changeOptions]);

  // 시스템 설정에서 옵션 초기화 (API 로딩 완료 후)
  useEffect(() => {
    if (!optionsInitialized && !settingsLoading && settings) {
      setChangeOptions({
        highlight: settings.shotHighlightEnabled ?? true,
        delta: settings.shotDeltaEnabled ?? false,
        sortByUpdate: settings.shotSortByUpdate ?? false,
      });
      setOptionsInitialized(true);
    }
  }, [settings, settingsLoading, optionsInitialized]);

  // 데이터 조회 (데모용 - mock API 사용)
  const fetchData = async (isAutoRefresh = false) => {
    try {
      if (isAutoRefresh) {
        // CSS 클래스로 애니메이션 처리 (state 대신 ref 사용으로 리렌더링 방지)
        if (refreshIndicatorRef.current) {
          refreshIndicatorRef.current.classList.add('refreshing');
          setTimeout(() => {
            refreshIndicatorRef.current?.classList.remove('refreshing');
          }, 1500);
        }
      } else {
        setLoading(true);
      }
      setError(null);

      const result = await fetchRealtimeShotStatus();

      if (!result.success) {
        throw new Error(result.message || 'API returned success: false');
      }

      const newData = result.data || [];

      // 변경 감지 - ref 사용으로 최신 상태 참조
      const currentShotData = shotDataRef.current;
      const currentOptions = changeOptionsRef.current;

      const changes = {};

      if (isAutoRefresh && currentShotData.length > 0) {
        // 자동 새로고침: 이전 프론트엔드 데이터와 비교
        newData.forEach((newRow) => {
          const prevRow = currentShotData.find(p => p.equipmentId === newRow.equipmentId);
          if (prevRow) {
            SHOT_COLUMNS.forEach((col) => {
              if (col.key !== 'equipmentName' && prevRow[col.key] !== newRow[col.key]) {
                const cellKey = `${newRow.equipmentId}-${col.key}`;
                changes[cellKey] = {
                  prev: prevRow[col.key],
                  curr: newRow[col.key],
                };
              }
            });
          }
        });
      } else if (!isAutoRefresh) {
        // 첫 로딩: 백엔드 previousShot과 비교
        newData.forEach((newRow) => {
          if (newRow.previousShot) {
            SHOT_COLUMNS.forEach((col) => {
              if (col.key !== 'equipmentName' && newRow.previousShot[col.key] !== undefined) {
                if (newRow.previousShot[col.key] !== newRow[col.key]) {
                  const cellKey = `${newRow.equipmentId}-${col.key}`;
                  changes[cellKey] = {
                    prev: newRow.previousShot[col.key],
                    curr: newRow[col.key],
                  };
                }
              }
            });
          }
        });
      }

      if (Object.keys(changes).length > 0) {
        setChangedCells(changes);
        // 7초 후 하이라이트 제거
        setTimeout(() => setChangedCells({}), 7000);
      }

      setPrevShotData(currentShotData);
      setShotData(newData);
      setLastUpdated(new Date());
    } catch (err) {
      console.error('Failed to fetch realtime shot data:', err);
      if (!isAutoRefresh) {
        setError('데이터를 불러오는데 실패했습니다.');
      }
    } finally {
      if (!isAutoRefresh) {
        setLoading(false);
      }
    }
  };

  // 초기 데이터 로드
  useEffect(() => {
    fetchData(false);
  }, []);

  // 자동 갱신 (일반 interval + ETL 폴링 모드 모두 지원)
  useAutoRefresh(() => {
    fetchData(true);
  });

  // 옵션 토글 핸들러 (DB에 저장)
  const handleOptionChange = async (optionKey) => {
    const newValue = !changeOptions[optionKey];

    // 로컬 상태 즉시 업데이트 (UI 반응성)
    setChangeOptions(prev => ({
      ...prev,
      [optionKey]: newValue,
    }));

    // DB 저장을 위한 필드명 매핑
    const fieldMap = {
      highlight: 'shotHighlightEnabled',
      delta: 'shotDeltaEnabled',
      sortByUpdate: 'shotSortByUpdate',
    };

    try {
      await saveSystemSettings({
        [fieldMap[optionKey]]: newValue,
      });
      // Context 갱신
      refreshSettings();
    } catch (err) {
      console.error('설정 저장 실패:', err);
    }
  };

  // 값 포맷팅
  const formatValue = (key, value) => {
    if (value === null || value === undefined) return '-';

    if (key === 'updateDateTime') {
      try {
        return new Date(value).toLocaleString('ko-KR');
      } catch {
        return value;
      }
    }

    if (typeof value === 'number') {
      // 온도, 압력, 속도 등은 소수점 1자리
      if (key.includes('Temp') || key.includes('Press') || key.includes('Speed')) {
        return value.toFixed(1);
      }
      // 위치, 시간은 소수점 2자리
      if (key.includes('Posi') || key.includes('Time')) {
        return value.toFixed(2);
      }
      return value.toLocaleString();
    }

    return value;
  };

  // 변화량 계산
  const getDelta = (equipmentId, key, currentValue, row) => {
    if (!changeOptions.delta) return null;
    if (currentValue === undefined || typeof currentValue !== 'number') return null;

    // 1. 먼저 프론트 prevShotData 확인 (새로고침 시)
    const prevRow = prevShotData.find(p => p.equipmentId === equipmentId);
    if (prevRow && prevRow[key] !== undefined && typeof prevRow[key] === 'number') {
      const diff = currentValue - prevRow[key];
      if (diff === 0) return null;
      return diff;
    }

    // 2. 프론트 prevShotData가 없으면 백엔드 previousShot 사용 (첫 로딩 시)
    if (row?.previousShot && row.previousShot[key] !== undefined && typeof row.previousShot[key] === 'number') {
      const diff = currentValue - row.previousShot[key];
      if (diff === 0) return null;
      return diff;
    }

    return null;
  };

  // 정렬된 데이터
  const getSortedData = () => {
    if (!changeOptions.sortByUpdate) return shotData;

    return [...shotData].sort((a, b) => {
      const dateA = new Date(a.updateDateTime || 0);
      const dateB = new Date(b.updateDateTime || 0);
      return dateB - dateA; // 최신순
    });
  };

  // 로딩 상태
  if (loading) {
    return (
      <MainLayout>
        <div className="realtime-shot-container">
          <div className="loading-state">
            <div className="loading-spinner"></div>
            <span>데이터를 불러오는 중...</span>
          </div>
        </div>
      </MainLayout>
    );
  }

  // 에러 상태
  if (error) {
    return (
      <MainLayout>
        <div className="realtime-shot-container">
          <div className="error-state">
            <span className="material-symbols-outlined">error</span>
            <span>{error}</span>
            <button onClick={() => fetchData(false)}>다시 시도</button>
          </div>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="realtime-shot-container">
        {/* 헤더 */}
        <div className="realtime-shot-header">
          <div className="realtime-shot-header-left">
            <div className="realtime-shot-title-group">
              <h1 className="realtime-shot-title">실시간 샷 현황</h1>
              <p className="realtime-shot-subtitle">사출기별 실시간 샷 현황</p>
            </div>
            <div className="change-options">
              {CHANGE_OPTIONS.map((option) => (
                <label key={option.key} className="change-option-label">
                  <input
                    type="checkbox"
                    checked={changeOptions[option.key]}
                    onChange={() => handleOptionChange(option.key)}
                  />
                  <span>{option.label}</span>
                </label>
              ))}
            </div>
          </div>
          <div className="realtime-shot-header-right" ref={refreshIndicatorRef}>
            <div className="update-label-container">
              <span className="update-label">업데이트</span>
              <span className="material-symbols-outlined refresh-icon">
                sync
              </span>
            </div>
            <div className="update-time">
              {lastUpdated.toLocaleString('ko-KR')}
            </div>
          </div>
        </div>

        {/* 테이블 영역 */}
        <div className="realtime-shot-content">
          <div className="shot-table-wrapper">
            <table className="shot-table">
              <thead>
                <tr>
                  {SHOT_COLUMNS.map((col) => (
                    <th key={col.key} className={col.fixed ? 'fixed-column' : ''}>
                      {col.label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {shotData.length === 0 ? (
                  <tr>
                    <td colSpan={SHOT_COLUMNS.length} className="empty-row">
                      데이터가 없습니다.
                    </td>
                  </tr>
                ) : (
                  getSortedData().map((row, index) => (
                    <tr key={row.equipmentId || index}>
                      {SHOT_COLUMNS.map((col) => {
                        const cellKey = `${row.equipmentId}-${col.key}`;
                        const isChanged = changedCells[cellKey];
                        const delta = getDelta(row.equipmentId, col.key, row[col.key], row);

                        // 셀 클래스 결정
                        let cellClass = col.fixed ? 'fixed-column' : '';
                        if (isChanged && changeOptions.highlight) {
                          cellClass += ' cell-changed';
                        }
                        if (delta && changeOptions.delta) {
                          cellClass += delta > 0 ? ' delta-up' : ' delta-down';
                        }

                        const displayValue = formatValue(col.key, row[col.key]);

                        return (
                          <td key={col.key} className={cellClass}>
                            <span className="cell-value">{displayValue}</span>
                            {delta && changeOptions.delta && (
                              <span className={`delta-badge ${delta > 0 ? 'up' : 'down'}`}>
                                {delta > 0 ? '▲' : '▼'}{Math.abs(delta).toFixed(1)}
                              </span>
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </MainLayout>
  );
}

export default RealtimeShotStatus;
